import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../../context/API';

// Types for Sales API Response
export interface SalesDataPoint {
  period: string;
  amount: number;
  count: number;
  averageOrderValue: number;
}

export interface SalesPeriod {
  startDate: string;
  endDate: string;
  granularity: string;
}

export interface SalesData {
  grossSales: number;
  netSales: number;
  refunds: number;
  tips: number;
  itemsSold: number;
  tax: number;
  totalRevenue: number;
  totalCost: number;
  grossProfit: number;
  profitMargin: number;
  currency: string;
  orderCount: number;
  period: SalesPeriod;
  salesData: SalesDataPoint[];
}

export interface SalesApiResponse {
  message: string;
  status: number;
  data: SalesData;
  success: boolean;
}

// Types for Item Sales API Response
export interface ItemSalesItem {
  item: string;
  totalTimesSold: number;
  totalRevenue: number;
  [key: string]: any;
}

export interface ItemSalesData {
  items: ItemSalesItem[];
  [key: string]: any;
}

export interface ItemSalesApiResponse {
  message: string;
  status: number;
  data: ItemSalesData;
  success: boolean;
}

// Request parameters
export interface SalesRequestParams {
  startDate?: string | Date;
  endDate?: string | Date;
  filter?: string;
  merchantId?: string;
  store?: string;
}

interface SalesDetailedState {
  salesData: SalesData | null;
  itemSalesData: ItemSalesData | null;
  monthlySalesData: number[];
  monthlyChange: number;
  loading: boolean;
  itemSalesLoading: boolean;
  monthlyLoading: boolean;
  error: string | null;
}

const initialState: SalesDetailedState = {
  salesData: null,
  itemSalesData: null,
  monthlySalesData: [],
  monthlyChange: 0,
  loading: false,
  itemSalesLoading: false,
  monthlyLoading: false,
  error: null,
};

// Async thunk to fetch sales data
export const fetchSalesData = createAsyncThunk<
  SalesData,
  SalesRequestParams,
  { rejectValue: string }
>('salesDetailed/fetchSalesData', async (params, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');
    
    const requestParams: any = {};
    
    if (params.startDate && params.endDate) {
      requestParams.startDate = typeof params.startDate === 'string' 
        ? params.startDate 
        : params.startDate instanceof Date
        ? params.startDate.toISOString().split('T')[0]
        : params.startDate;
      requestParams.endDate = typeof params.endDate === 'string'
        ? params.endDate
        : params.endDate instanceof Date
        ? params.endDate.toISOString().split('T')[0]
        : params.endDate;
    }
    
    if (params.filter) {
      requestParams.filter = params.filter;
    }
    
    if (params.merchantId) {
      requestParams.merchantId = params.merchantId;
    }
    
    if (params.store) {
      requestParams.store = params.store;
    }

    const response = await API.get<SalesApiResponse>('/analytics/sales', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: requestParams,
    });

    if (response.data.success && response.data.data) {
      return response.data.data;
    } else {
      return rejectWithValue('Failed to fetch sales data');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch sales data'
    );
  }
});

// Async thunk to fetch item sales data
export const fetchItemSalesData = createAsyncThunk<
  ItemSalesData,
  SalesRequestParams,
  { rejectValue: string }
>('salesDetailed/fetchItemSalesData', async (params, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');
    
    const requestParams: any = {};
    
    if (params.startDate && params.endDate) {
      requestParams.startDate = typeof params.startDate === 'string'
        ? params.startDate
        : params.startDate instanceof Date
        ? params.startDate.toISOString().split('T')[0]
        : params.startDate;
      requestParams.endDate = typeof params.endDate === 'string'
        ? params.endDate
        : params.endDate instanceof Date
        ? params.endDate.toISOString().split('T')[0]
        : params.endDate;
    }
    
    if (params.filter) {
      requestParams.filter = params.filter;
    }
    
    if (params.merchantId) {
      requestParams.merchantId = params.merchantId;
    }
    
    if (params.store) {
      requestParams.store = params.store;
    }

    const response = await API.get<ItemSalesApiResponse>('/analytics/item-sales', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: requestParams,
    });

    if (response.data.success && response.data.data) {
      return response.data.data;
    } else {
      return rejectWithValue('Failed to fetch item sales data');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch item sales data'
    );
  }
});

// Async thunk to fetch monthly sales data for trend calculation
export const fetchMonthlySalesData = createAsyncThunk<
  { monthlyData: number[]; monthlyChange: number },
  { storeId?: string },
  { rejectValue: string }
>('salesDetailed/fetchMonthlySalesData', async ({ storeId }, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');
    const salesDataByMonth: number[] = [];
    const promises = [];

    const today = new Date();
    const monthsToFetch = 5;

    for (let i = monthsToFetch - 1; i >= 0; i--) {
      const monthDate = new Date(
        today.getFullYear(),
        today.getMonth() - i,
        1
      );
      const startDate = new Date(
        monthDate.getFullYear(),
        monthDate.getMonth(),
        1
      );
      const endDate = new Date(
        monthDate.getFullYear(),
        monthDate.getMonth() + 1,
        0
      );

      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      const promise = API.get<SalesApiResponse>('/analytics/sales', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          startDate: startDateStr,
          endDate: endDateStr,
          store: storeId,
        },
      });

      promises.push(promise);
    }

    const responses = await Promise.all(promises);

    responses.forEach((response) => {
      const netSales = parseFloat(response.data.data.netSales) || 0;
      salesDataByMonth.push(netSales);
    });

    let monthlyChange = 0;
    if (salesDataByMonth.length >= 2) {
      const lastMonthSales = salesDataByMonth[salesDataByMonth.length - 1];
      const prevMonthSales = salesDataByMonth[salesDataByMonth.length - 2];
      monthlyChange = prevMonthSales !== 0
        ? parseFloat((((lastMonthSales - prevMonthSales) / prevMonthSales) * 100).toFixed(2))
        : 0;
    }

    return {
      monthlyData: salesDataByMonth,
      monthlyChange,
    };
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch monthly sales data'
    );
  }
});

const salesDetailedSlice = createSlice({
  name: 'salesDetailed',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSalesData: (state) => {
      state.salesData = null;
    },
    clearItemSalesData: (state) => {
      state.itemSalesData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch sales data
      .addCase(fetchSalesData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSalesData.fulfilled, (state, action) => {
        state.loading = false;
        state.salesData = action.payload;
      })
      .addCase(fetchSalesData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch item sales data
      .addCase(fetchItemSalesData.pending, (state) => {
        state.itemSalesLoading = true;
        state.error = null;
      })
      .addCase(fetchItemSalesData.fulfilled, (state, action) => {
        state.itemSalesLoading = false;
        state.itemSalesData = action.payload;
      })
      .addCase(fetchItemSalesData.rejected, (state, action) => {
        state.itemSalesLoading = false;
        state.error = action.payload as string;
      })
      // Fetch monthly sales data
      .addCase(fetchMonthlySalesData.pending, (state) => {
        state.monthlyLoading = true;
        state.error = null;
      })
      .addCase(fetchMonthlySalesData.fulfilled, (state, action) => {
        state.monthlyLoading = false;
        state.monthlySalesData = action.payload.monthlyData;
        state.monthlyChange = action.payload.monthlyChange;
      })
      .addCase(fetchMonthlySalesData.rejected, (state, action) => {
        state.monthlyLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSalesData, clearItemSalesData } = salesDetailedSlice.actions;
export default salesDetailedSlice.reducer;
